<template>
  <div
    class="app"
    :class="{ 'landing-mode': isLandingPage }"
    :data-theme="isDarkMode ? 'dark' : 'light'"
  >
    <navigation-bar v-if="!hideHeader" />
    <main
      class="main-content"
      :class="{ 'with-header': !hideHeader }"
    >
      <router-view v-slot="{ Component, route: viewRoute }">
        <transition
          name="page"
          mode="out-in"
        >
          <component
            :is="Component"
            :key="viewRoute.path"
          />
        </transition>
      </router-view>
    </main>
    <app-footer v-if="!isLandingPage" />

    <AppleRelayEmailModal v-model="showAppleRelayModal" />
    <BetaSiteModal v-model="showBetaModal" />
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import { setLanguage } from '@/i18n';
import NavigationBar from '@/components/layout/NavigationBar.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import AppleRelayEmailModal from '@/components/ui/AppleRelayEmailModal.vue';
import BetaSiteModal from '@/components/ui/BetaSiteModal.vue';
import { useThemeStore } from '@/store/theme';
import { useAuthStore } from '@/store/auth';
import {
  clearBetaPreference,
  hasBetaPreference,
  isNoBetaQuery,
} from '@/utils/betaSite';

// Theme handling
const themeStore = useThemeStore();
const authStore = useAuthStore();
const isDarkMode = computed(() => themeStore.isDark);

// Determine if current page is landing page
const route = useRoute();
const hideHeader = computed(() => route.meta?.noHeader === true);
const isLandingPage = ref(false);
const showAppleRelayModal = ref(false);
const showBetaModal = ref(false);

if (localStorage.getItem('utm_campaign') === 'undefined') {
  localStorage.removeItem('utm_campaign');
}

if (localStorage.getItem('utm_medium') === 'undefined') {
  localStorage.removeItem('utm_medium');
}

if (localStorage.getItem('utm_source') === 'undefined') {
  localStorage.removeItem('utm_source');
}

// Watch for query params and store referral if present
watch(
  () => route.query,
  (newQuery) => {
    if (newQuery.ref) {
      localStorage.setItem('referral', newQuery.ref);
    }

    if (newQuery.from) {
      localStorage.setItem('utm_campaign', newQuery.from);
    }

    if (newQuery.utm_campaign) {
      localStorage.setItem('utm_campaign', newQuery.utm_campaign);
    }

    if (newQuery.utm_medium) {
      localStorage.setItem('utm_medium', newQuery.utm_medium);
    }

    if (newQuery.utm_source) {
      localStorage.setItem('utm_source', newQuery.utm_source);
    }

    if (newQuery.lang) {
      setLanguage(newQuery.lang);
    }

    if (isNoBetaQuery(newQuery.nobeta)) {
      clearBetaPreference();
    }
  },
  { immediate: true },
);

// Watch for auth changes to enforce theme restrictions
watch(
  () => authStore.currentUser,
  () => {
    // Enforce light mode for non-PRO users when auth state changes
    themeStore.enforceLightModeForBasicUsers();
  },
  { immediate: false },
);

// Check for Apple private relay email after user is loaded
watch(
  () => authStore.currentUser,
  (user) => {

    if (
      user?.email?.includes('privaterelay.appleid.com')
    ) {
      showAppleRelayModal.value = true;
    }
  },
  { immediate: true },
);

// Initial theme setup
// `main.js` synchronously applies the dark classes (only when the cached
// user is PRO), preventing FOUC. Here we sync the Pinia store and let
// `setDarkMode` perform its full action: PRO enforcement, state update,
// localStorage persistence, and DOM class toggling. For non-PRO users the
// classes were never applied in `main.js`, so there is no visible flash.
onMounted(() => {
  if (!hasBetaPreference()) {
    showBetaModal.value = true;
  }

  const savedTheme = localStorage.getItem('indiefy-theme');
  if (savedTheme === 'dark') {
    themeStore.setDarkMode(true);
  }
  themeStore.enforceLightModeForBasicUsers();
});
</script>

<style>
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.body--dark .label {
  color: white;
}

.pro-artists-carousel .carousel__viewport {
  padding-top: 1rem;
  padding-bottom: 1rem;
  overflow: visible !important;
}

@media (max-width: 600px) {
  .pro-artists-carousel .carousel__slide .artist-card {
    border-radius: 0 !important;
  }
}
</style>